import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid2,
    TextField,
    Box,
    Typography,
} from '@mui/material';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import { getVenue } from '@openapi/venues/venues';
import useNotify from '../hooks/useNotify';
import RichTextEditor from '../components/richtexteditor/RichTextEditor';

function RichTextTranslationsModal({
    showModal,
    MODAL_TYPE,
    translations,
    setTranslations,
    item,
    column,
    intermediateColumn = null, // optional
    venueId,
    originalContent, // TipTap JSON content
}) {
    const { t } = useTranslation();
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [localTranslations, setLocalTranslations] = useState(translations || {});
    const { notifyError } = useNotify();

    // default to "columns.<column>" if intermediateColumn is not provided
    const itemLabel = intermediateColumn
        ? t(`${item}.${intermediateColumn}.${column}`)
        : t(`${item}.columns.${column}`);

    const getAvailableLanguages = useCallback(async () => {
        try {
            const result = await getVenue(Number(venueId));
            setAvailableLanguages(result?.availableLanguages || []);
        } catch (error) {
            notifyError(error);
        }
    }, [venueId, notifyError]);

    useEffect(() => {
        getAvailableLanguages();
    }, [venueId, getAvailableLanguages]);

    // Helper function to get a default TipTap document structure
    const getDefaultTipTapContent = () => ({
        type: 'doc',
        content: [
            {
                type: 'paragraph',
                content: [],
            },
        ],
    });

    // Helper function to check if TipTap content is empty
    const isContentEmpty = (content) => {
        if (!content || !content.content) return true;

        return content.content.every((node) => {
            if (node.type === 'paragraph') {
                return (
                    !node.content ||
                    node.content.length === 0 ||
                    node.content.every(
                        (child) =>
                            child.type === 'text' && (!child.text || child.text.trim() === ''),
                    )
                );
            }
            return false;
        });
    };

    return (
        <Dialog
            aria-labelledby="rich-text-translations-modal"
            open
            maxWidth="lg"
            fullWidth
            onClose={() => showModal(MODAL_TYPE.NONE)}
        >
            <DialogTitle>
                {itemLabel} ({t('common.translations')})
            </DialogTitle>
            <DialogContent>
                <Grid2 container spacing={2}>
                    <DialogContentText id="alert-dialog-info">
                        <Alert sx={{ mt: 0.5, mb: 2 }} severity="info">
                            {t('common.languageInfo')}
                        </Alert>
                    </DialogContentText>

                    {/* Original Content Display */}
                    <Grid2 size={12}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                {`${itemLabel} (${t('common.original')})`}
                            </Typography>
                            <Box
                                sx={{
                                    border: '1px solid #c4c4c4',
                                    borderRadius: 1,
                                    minHeight: 100,
                                    px: 2,
                                    py: 1,
                                    background: '#f5f5f5',
                                    opacity: 0.7,
                                }}
                            >
                                <RichTextEditor
                                    value={originalContent}
                                    onChange={() => {}} // Read-only
                                    label=""
                                />
                            </Box>
                        </Box>
                    </Grid2>

                    {/* Translation Fields */}
                    {availableLanguages.map((language) => (
                        <Grid2 key={language} size={12}>
                            <Grid2 container spacing={2} alignItems="flex-start">
                                <Grid2 size={{ xs: 12, md: 3 }}>
                                    <Autocomplete
                                        options={[language]}
                                        value={language}
                                        disabled
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('common.language')}
                                                fullWidth
                                                sx={{ mt: 1 }}
                                            />
                                        )}
                                        getOptionLabel={(option) => option}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 9 }}>
                                    <RichTextEditor
                                        value={
                                            localTranslations[language] &&
                                            !isContentEmpty(localTranslations[language])
                                                ? localTranslations[language]
                                                : getDefaultTipTapContent()
                                        }
                                        onChange={(content) => {
                                            setLocalTranslations({
                                                ...localTranslations,
                                                [language]: content,
                                            });
                                        }}
                                        label={`${itemLabel} (${language})`}
                                    />
                                </Grid2>
                            </Grid2>
                        </Grid2>
                    ))}
                </Grid2>
            </DialogContent>
            <DialogActions>
                <CommonButton
                    variant="inherit"
                    onClick={() => showModal(MODAL_TYPE.NONE)}
                    label={t('common.cancel')}
                    buttonType="secondary"
                />
                <CommonButton
                    onClick={() => {
                        // Filter out empty translations before saving
                        const filteredTranslations = {};
                        Object.keys(localTranslations).forEach((language) => {
                            const content = localTranslations[language];
                            if (content && !isContentEmpty(content)) {
                                filteredTranslations[language] = content;
                            }
                        });

                        setTranslations(filteredTranslations);
                        showModal(MODAL_TYPE.NONE);
                    }}
                    label={t('common.set')}
                    buttonType="primary"
                />
            </DialogActions>
        </Dialog>
    );
}

export default RichTextTranslationsModal;
